/**
 * RM Inward — Inward Without Invoice tab.
 *
 * Parallel flow to the standard "Inward with invoice" path. Stores teams can
 * receive RM stock without an invoice; later they can either Cancel (reverses
 * stock) or Create Invoice (creates a Zoho-backed bill that does NOT re-credit
 * stock).
 *
 * Backend: see /app/backend/routes/rm_inward_no_invoice_routes.py
 * Status:  DRAFT → SUBMITTED → (CANCELLED | INVOICED)
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit2, Send, X, FileText, Trash2, Loader2, Search, Layers, Eye, Upload, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import Pagination from "@/components/Pagination";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PermButton from "@/components/PermButton";
import BulkInvoiceCreateFromFileDialog from "@/components/BulkInvoiceCreateFromFileDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  DRAFT: "bg-stone-100 text-stone-700 border-stone-300",
  SUBMITTED: "bg-emerald-100 text-emerald-800 border-emerald-300",
  CANCELLED: "bg-rose-100 text-rose-800 border-rose-300",
  INVOICED: "bg-sky-100 text-sky-800 border-sky-300",
};

const EMPTY_LINE = { rm_id: "", description: "", quantity: "", unit: "" };

// Feb 2026 — timeline filter helpers. The date filter narrows the
// `entry_date` field on the backend using string-lex comparison; the
// selected preset just decides which `from`/`to` we hand over.
const DATE_MODES = { LAST_90: "last_90", MONTH: "month", RANGE: "range", ALL: "all" };
const _iso = (d) => d.toISOString().slice(0, 10); // YYYY-MM-DD

function defaultTimeline() {
  // Rolling last-90-days window ending today (inclusive).
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 90);
  return {
    mode: DATE_MODES.LAST_90,
    month: _iso(today).slice(0, 7),   // YYYY-MM (for month picker)
    from: _iso(from),
    to: _iso(today),
  };
}

/** Compute {from, to} to hit the backend with (undefined = no bound).
 *  Returns YYYY-MM-DD strings. Month mode expands YYYY-MM to full
 *  first-day / last-day span. */
function resolveDateRange(tl) {
  if (!tl || tl.mode === DATE_MODES.ALL) return { from: undefined, to: undefined };
  if (tl.mode === DATE_MODES.LAST_90 || tl.mode === DATE_MODES.RANGE) {
    return { from: tl.from || undefined, to: tl.to || undefined };
  }
  if (tl.mode === DATE_MODES.MONTH && tl.month) {
    // Expand YYYY-MM into first and last day of that month.
    const [yStr, mStr] = tl.month.split("-");
    const y = parseInt(yStr, 10);
    const m = parseInt(mStr, 10);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const last = new Date(Date.UTC(y, m, 0)); // day 0 of next month = last day of this month
    return { from: _iso(first), to: _iso(last) };
  }
  return { from: undefined, to: undefined };
}

/** Human-readable label of the current window — surfaced next to the
 *  filter row so users can never wonder "did old data disappear?". */
function describeTimeline(tl) {
  if (!tl || tl.mode === DATE_MODES.ALL) return "All time";
  if (tl.mode === DATE_MODES.LAST_90) return `Last 90 days (${tl.from} → ${tl.to})`;
  if (tl.mode === DATE_MODES.MONTH) return tl.month ? `Month: ${tl.month}` : "Month";
  if (tl.mode === DATE_MODES.RANGE) return `${tl.from || "…"} → ${tl.to || "…"}`;
  return "All time";
}

export default function RMInwardNoInvoice({ vendors = [], branches = [], availableRMs = [], onSwitchTab, onStartCreateInvoice }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "ALL", branch: "ALL", search: "" });
  // Feb 2026 — pagination + timeline. Server-side paging so every
  // entry is reachable across pages regardless of collection size.
  const [timeline, setTimeline] = useState(defaultTimeline);
  const [page, setPage] = useState(1);        // 1-indexed
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(buildEmptyForm());
  const [submitting, setSubmitting] = useState(false);

  const [actionTarget, setActionTarget] = useState(null); // {entry, action}
  const [cancelReason, setCancelReason] = useState("");
  // Feb 2026 — read-only view dialog for any entry (DRAFT/SUBMITTED/CANCELLED).
  const [viewEntry, setViewEntry] = useState(null);

  // Multi-select state for bulk → invoice handoff. Only SUBMITTED entries
  // are selectable. The bulk action validates same-vendor/same-branch via
  // /api/rm-inward/entries/aggregate-for-invoice and then hands the
  // aggregated payload to the with-invoice tab via onStartCreateInvoice.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Jul 2026 — second entry point to the bulk consolidation flow.
  // File upload → parse entry IDs → same aggregate-for-invoice endpoint
  // the checkbox path uses → same `onStartCreateInvoice` handoff. See
  // `BulkInvoiceCreateFromFileDialog.jsx`.
  const [showBulkFileDialog, setShowBulkFileDialog] = useState(false);
  const [bulkPreparing, setBulkPreparing] = useState(false);

  // ------------- Load -------------
  const loadEntries = async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * pageSize, limit: pageSize };
      if (filters.status !== "ALL") params.status = filters.status;
      if (filters.branch !== "ALL") params.branch = filters.branch;
      if (filters.search.trim()) params.bill_number = filters.search.trim();
      const { from, to } = resolveDateRange(timeline);
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await axios.get(`${API}/rm-inward/entries`, { params });
      setEntries(data.items || []);
      setTotal(typeof data.total === "number" ? data.total : (data.items || []).length);
    } catch (e) {
      toast.error("Failed to load no-invoice entries");
    } finally {
      setLoading(false);
    }
  };
  // Any filter change (status / branch / search / timeline) resets to
  // page 1 — never leave the user on "page 4 of 2".
  useEffect(() => { setPage(1); }, [filters.status, filters.branch, filters.search, timeline]);
  // Fetch on page/pageSize/filter changes. `timeline` and `filters`
  // are captured inside loadEntries; explicit deps keep the linter
  // honest but the actual triggers are: filters, timeline, page, pageSize.
  useEffect(() => { loadEntries(); }, [filters.status, filters.branch, filters.search, timeline, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // ------------- Filtered RMs for picker -------------
  const [rmSearch, setRmSearch] = useState("");
  const filteredRMs = useMemo(() => {
    const q = rmSearch.trim().toLowerCase();
    if (!q) return availableRMs.slice(0, 50);
    return availableRMs.filter((r) =>
      (r.rm_id || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q) ||
      (r.name || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [rmSearch, availableRMs]);

  // ------------- Create / Edit handlers -------------
  const openCreate = () => {
    setEditingId(null);
    setForm(buildEmptyForm());
    setShowCreate(true);
  };
  const openEdit = (entry) => {
    setEditingId(entry.entry_id);
    setForm({
      vendor_id: entry.vendor_id || "",
      vendor_name: entry.vendor_name || "",
      branch: entry.branch || "",
      branch_id: entry.branch_id || "",
      bill_number: entry.bill_number || "",
      entry_date: (entry.entry_date || "").slice(0, 10),
      notes: entry.notes || "",
      line_items: entry.line_items?.length
        ? entry.line_items.map((li) => ({
            rm_id: li.rm_id, description: li.description || "",
            quantity: String(li.quantity), unit: li.unit || "",
          }))
        : [{ ...EMPTY_LINE }],
    });
    setShowCreate(true);
  };

  const onVendorChange = (vendor_id) => {
    const v = vendors.find((x) => x.vendor_id === vendor_id);
    setForm((f) => ({ ...f, vendor_id, vendor_name: v?.name || v?.vendor_name || "" }));
  };
  const onBranchChange = (branch) => {
    const b = branches.find((x) => x.name === branch);
    setForm((f) => ({ ...f, branch, branch_id: b?.branch_id || "" }));
  };

  const setLine = (idx, patch) => {
    setForm((f) => {
      const next = [...f.line_items];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, line_items: next };
    });
  };
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE }] }));
  const addFreightLine = () => setForm((f) => ({
    ...f,
    line_items: [...f.line_items, {
      rm_id: "FREIGHT", description: "Freight", quantity: "1", unit: "lump sum",
    }],
  }));
  const removeLine = (idx) =>
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));
  const pickRMForLine = (idx, rm) =>
    setLine(idx, { rm_id: rm.rm_id, description: rm.description || rm.name || "", unit: rm.uom || rm.procurement_uom || "" });

  const saveEntry = async (alsoSubmit = false) => {
    const errors = validateForm(form);
    if (errors.length) { toast.error(errors[0]); return; }
    setSubmitting(true);
    try {
      const payload = {
        vendor_id: form.vendor_id,
        vendor_name: form.vendor_name,
        branch: form.branch,
        branch_id: form.branch_id || undefined,
        bill_number: form.bill_number.trim(),
        entry_date: form.entry_date || undefined,
        notes: form.notes || undefined,
        line_items: form.line_items
          .filter((li) => li.rm_id && li.quantity)
          .map((li) => ({
            rm_id: li.rm_id,
            description: li.description || undefined,
            quantity: Number(li.quantity),
            unit: li.unit || undefined,
          })),
      };
      let savedId = editingId;
      if (editingId) {
        await axios.put(`${API}/rm-inward/entries/${editingId}`, payload);
        toast.success(`Entry ${editingId} updated`);
      } else {
        const { data } = await axios.post(`${API}/rm-inward/entries`, payload);
        savedId = data.entry_id;
        toast.success(`Draft entry ${data.entry_id} created`);
      }
      // One-click "Submit" path: chain a submit call right after the save.
      if (alsoSubmit && savedId) {
        try {
          const { data: subbed } = await axios.post(`${API}/rm-inward/entries/${savedId}/submit`);
          toast.success(`Entry ${subbed.entry_id} submitted — stock credited to ${subbed.branch}`);
        } catch (e) {
          const d = e.response?.data?.detail;
          toast.error(typeof d === "string" ? d : "Submit failed (entry kept as DRAFT)", { duration: 9000 });
        }
      }
      setShowCreate(false);
      loadEntries();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save entry");
    } finally {
      setSubmitting(false);
    }
  };

  // ------------- Per-row actions -------------
  const doSubmit = async (entry) => {
    try {
      await axios.post(`${API}/rm-inward/entries/${entry.entry_id}/submit`);
      toast.success(`Entry ${entry.entry_id} submitted — stock credited to ${entry.branch}`);
      loadEntries();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submit failed");
    }
  };

  const doCancel = async () => {
    if (!actionTarget?.entry) return;
    try {
      await axios.post(`${API}/rm-inward/entries/${actionTarget.entry.entry_id}/cancel`, {
        reason: cancelReason || undefined,
      });
      toast.success(`Entry ${actionTarget.entry.entry_id} cancelled — stock reversed`);
      setActionTarget(null); setCancelReason("");
      loadEntries();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Cancel failed");
    }
  };

  const doDelete = async () => {
    if (!actionTarget?.entry) return;
    try {
      await axios.delete(`${API}/rm-inward/entries/${actionTarget.entry.entry_id}`);
      toast.success(`Draft entry ${actionTarget.entry.entry_id} deleted`);
      setActionTarget(null);
      loadEntries();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    }
  };

  const doCreateInvoice = async (entry) => {
    // Hands the entry's vendor/branch/bill-number/line-items to the
    // "Inward with invoice" tab, which opens its create-bill dialog
    // pre-populated. The user fills tax/TDS/freight/discount/attachments
    // and clicks Save — the existing flow handles the Zoho push and the
    // backend marks this entry INVOICED via `linked_no_invoice_entry_id`.
    if (typeof onStartCreateInvoice === "function") {
      onStartCreateInvoice(entry);
      toast.info(
        `Switched to "Inward with invoice". Vendor, branch, and RM lines are locked to entry ${entry.entry_id}. Enter the actual vendor invoice number in Bill #.`,
        { duration: 9000 }
      );
    } else if (typeof onSwitchTab === "function") {
      onSwitchTab("with-invoice");
    }
  };

  // ------------- Bulk multi-entry → single invoice handoff -------------
  // Eligibility for selection: SUBMITTED only (DRAFT can't merge, CANCELLED/
  // INVOICED are terminal). The Master checkbox in the table header only
  // toggles the SUBMITTED rows on the current filtered page.
  const submittedRows = useMemo(
    () => entries.filter((e) => e.status === "SUBMITTED"),
    [entries]
  );
  const allSubmittedSelected = submittedRows.length > 0 &&
    submittedRows.every((e) => selectedIds.has(e.entry_id));

  const toggleRow = (entry_id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entry_id)) next.delete(entry_id);
      else next.add(entry_id);
      return next;
    });
  };
  const toggleAllSubmitted = () => {
    setSelectedIds((prev) => {
      if (allSubmittedSelected) return new Set();
      const next = new Set(prev);
      submittedRows.forEach((e) => next.add(e.entry_id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Clear any stale selections every time the filtered list changes — a row
  // that was selected but has since dropped off the filter set shouldn't
  // silently make it into the bulk handoff.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(entries.map((e) => e.entry_id));
      const next = new Set();
      prev.forEach((id) => { if (visible.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [entries]);

  const doBulkCreateInvoice = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    // Cheap client-side preflight before bothering the backend — gives a
    // friendlier error when the user picked rows from different vendors
    // or branches.
    const picked = entries.filter((e) => selectedIds.has(e.entry_id));
    const vendors = new Set(picked.map((e) => e.vendor_id));
    const branches = new Set(picked.map((e) => e.branch));
    if (vendors.size > 1) {
      toast.error("Selected entries must share the same vendor.");
      return;
    }
    if (branches.size > 1) {
      toast.error("Selected entries must share the same branch.");
      return;
    }
    setBulkPreparing(true);
    try {
      const { data } = await axios.post(`${API}/rm-inward/entries/aggregate-for-invoice`, {
        entry_ids: ids,
      });
      // Reuse the existing single-entry handoff plumbing. The synthetic
      // payload carries `entry_ids: [...]` which RMInward.js will forward
      // as `linked_no_invoice_entry_ids` (plural) when creating the bill.
      if (typeof onStartCreateInvoice === "function") {
        onStartCreateInvoice(data);
        toast.info(
          `Consolidated ${ids.length} entries into one invoice draft. ` +
          `Review tax / freight / discount on the with-invoice tab, then Save.`,
          { duration: 9000 }
        );
        clearSelection();
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to aggregate entries", { duration: 9000 });
    } finally {
      setBulkPreparing(false);
    }
  };

  // ------------- Render -------------
  return (
    <div className="space-y-4" data-testid="rm-inward-no-invoice-tab">
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-[160px]" data-testid="noinv-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="SUBMITTED">Submitted</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                <SelectItem value="INVOICED">Invoiced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase">Branch</Label>
            <Select value={filters.branch} onValueChange={(v) => setFilters({ ...filters, branch: v })}>
              <SelectTrigger className="w-[200px]" data-testid="noinv-filter-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.branch_id || b.name} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search by bill number…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="h-9"
              data-testid="noinv-filter-search"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.size > 0 && (
              <>
                <Badge variant="outline" className="text-xs" data-testid="noinv-selection-count">
                  {selectedIds.size} selected
                </Badge>
                <Button
                  size="sm" variant="ghost" onClick={clearSelection}
                  data-testid="noinv-clear-selection"
                >
                  Clear
                </Button>
                <PermButton
                  entity="RMInwardBill" action="CREATE"
                  onClick={doBulkCreateInvoice}
                  disabled={bulkPreparing || selectedIds.size < 1}
                  data-testid="noinv-bulk-invoice-btn"
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  {bulkPreparing
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Layers className="w-4 h-4 mr-2" />}
                  Create Invoice from {selectedIds.size} {selectedIds.size === 1 ? "entry" : "entries"}
                </PermButton>
              </>
            )}
            <PermButton
              entity="RMInwardBill" action="CREATE"
              onClick={() => setShowBulkFileDialog(true)}
              variant="outline"
              data-testid="noinv-bulk-file-btn"
              title="Consolidate many no-invoice entries into one draft by uploading their entry IDs"
            >
              <Upload className="w-4 h-4 mr-2" /> Bulk Invoice Create
            </PermButton>
            <PermButton
              entity="RMInwardBill" action="CREATE"
              onClick={openCreate} data-testid="noinv-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New No-Invoice Entry
            </PermButton>
          </div>
        </CardContent>
      </Card>

      {/* Feb 2026 — Timeline filter row. Default = last 90 days
          rolling. The active window label is always visible so no
          one thinks history disappeared; "All time" is one click. */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-stone-400" />
            <Label className="text-xs uppercase">Timeline</Label>
            <Select
              value={timeline.mode}
              onValueChange={(v) =>
                setTimeline((tl) =>
                  v === DATE_MODES.LAST_90 ? defaultTimeline()
                  : { ...tl, mode: v }
                )
              }
            >
              <SelectTrigger className="w-[180px]" data-testid="noinv-timeline-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DATE_MODES.LAST_90} data-testid="noinv-timeline-last90">Last 90 days</SelectItem>
                <SelectItem value={DATE_MODES.MONTH} data-testid="noinv-timeline-month-opt">By month</SelectItem>
                <SelectItem value={DATE_MODES.RANGE} data-testid="noinv-timeline-range-opt">Date range</SelectItem>
                <SelectItem value={DATE_MODES.ALL} data-testid="noinv-timeline-all-opt">All time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {timeline.mode === DATE_MODES.MONTH && (
            <Input
              type="month"
              value={timeline.month}
              onChange={(e) => setTimeline((tl) => ({ ...tl, month: e.target.value }))}
              className="h-9 w-[160px]"
              data-testid="noinv-timeline-month"
            />
          )}

          {timeline.mode === DATE_MODES.RANGE && (
            <>
              <div className="flex items-center gap-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={timeline.from}
                  onChange={(e) => setTimeline((tl) => ({ ...tl, from: e.target.value }))}
                  className="h-9 w-[150px]"
                  data-testid="noinv-timeline-from"
                />
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={timeline.to}
                  onChange={(e) => setTimeline((tl) => ({ ...tl, to: e.target.value }))}
                  className="h-9 w-[150px]"
                  data-testid="noinv-timeline-to"
                />
              </div>
            </>
          )}

          {/* Active window label — always shown so history is never
              "silently hidden" behind the default 90-day scope. */}
          <Badge
            variant={timeline.mode === DATE_MODES.ALL ? "outline" : "secondary"}
            className="ml-1 font-mono text-xs"
            data-testid="noinv-timeline-label"
          >
            {describeTimeline(timeline)}
          </Badge>

          {timeline.mode !== DATE_MODES.ALL && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setTimeline({ ...timeline, mode: DATE_MODES.ALL })}
              className="text-stone-600"
              data-testid="noinv-timeline-clear"
            >
              Show all time
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSubmittedSelected}
                    onCheckedChange={toggleAllSubmitted}
                    disabled={submittedRows.length === 0}
                    aria-label="Select all submitted entries"
                    data-testid="noinv-select-all"
                  />
                </TableHead>
                <TableHead className="w-[130px]">Entry ID</TableHead>
                <TableHead className="w-[110px]">Bill #</TableHead>
                <TableHead className="w-[180px]">Vendor</TableHead>
                <TableHead className="w-[120px]">Branch</TableHead>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead>RM Items</TableHead>
                <TableHead className="w-[90px]">Status</TableHead>
                <TableHead className="w-[110px]">Linked bill</TableHead>
                <TableHead className="w-[90px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-stone-500">
                  <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…
                </TableCell></TableRow>
              )}
              {!loading && entries.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center py-12 text-stone-500" data-testid="noinv-empty">
                  No no-invoice entries match these filters.
                </TableCell></TableRow>
              )}
              {entries.map((e) => (
                <TableRow key={e.entry_id} data-testid={`noinv-row-${e.entry_id}`}>
                  <TableCell>
                    {e.status === "SUBMITTED" ? (
                      <Checkbox
                        checked={selectedIds.has(e.entry_id)}
                        onCheckedChange={() => toggleRow(e.entry_id)}
                        aria-label={`Select ${e.entry_id}`}
                        data-testid={`noinv-select-${e.entry_id}`}
                      />
                    ) : (
                      <span className="text-stone-300 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    <div className="max-w-[130px] truncate" title={e.entry_id}>{e.entry_id}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="max-w-[110px] truncate" title={e.bill_number}>{e.bill_number}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="max-w-[180px] truncate" title={e.vendor_name}>{e.vendor_name}</div>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="max-w-[120px] truncate" title={e.branch}>{e.branch}</div>
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{(e.entry_date || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-xs" data-testid={`noinv-items-${e.entry_id}`}>
                    {(e.line_items || []).length === 0 ? (
                      <span className="text-stone-400">—</span>
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        {(e.line_items || []).map((li, i) => (
                          <div key={i} className="flex items-baseline gap-2 leading-tight">
                            <span className="font-mono text-[11px]">{li.rm_id}</span>
                            <span className="text-stone-500 text-[11px]">× {li.quantity}{li.unit ? ` ${li.unit}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell><Badge className={`text-[10px] ${STATUS_STYLES[e.status] || ""}`}>{e.status}</Badge></TableCell>
                  <TableCell className="text-xs font-mono">{e.linked_bill_id || "—"}</TableCell>
                  <TableCell className="text-right">
                    <RowActions
                      entry={e}
                      onView={() => setViewEntry(e)}
                      onEdit={() => openEdit(e)}
                      onDeleteAsk={() => setActionTarget({ entry: e, action: "delete" })}
                      onSubmit={() => doSubmit(e)}
                      onCancelAsk={() => setActionTarget({ entry: e, action: "cancel" })}
                      onCreateInvoice={() => doCreateInvoice(e)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
        {/* Feb 2026 — pagination footer. Drives from server totals so
            every entry is reachable regardless of collection size.
            Page-size options include 100 so power users can still
            grab a large chunk in one shot. */}
        <div className="px-4 py-2 border-t">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            pageSizeOptions={[10, 25, 50, 100]}
            onPageChange={setPage}
            onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
            loading={loading}
          />
        </div>
      </Card>

      <EntryDialog
        open={showCreate}
        editingId={editingId}
        form={form} setForm={setForm}
        vendors={vendors} branches={branches}
        onVendorChange={onVendorChange} onBranchChange={onBranchChange}
        setLine={setLine} addLine={addLine} addFreightLine={addFreightLine} removeLine={removeLine}
        rmSearch={rmSearch} setRmSearch={setRmSearch} filteredRMs={filteredRMs}
        pickRMForLine={pickRMForLine}
        onClose={() => setShowCreate(false)}
        onSaveDraft={() => saveEntry(false)}
        onSaveAndSubmit={() => saveEntry(true)}
        submitting={submitting}
      />

      <Dialog open={actionTarget?.action === "cancel"} onOpenChange={(o) => !o && setActionTarget(null)}>
        <DialogContent data-testid="noinv-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel entry {actionTarget?.entry?.entry_id}?</DialogTitle>
            <DialogDescription>
              This will reverse all stock movements credited to{" "}
              <strong>{actionTarget?.entry?.branch}</strong>. The action is logged in
              <code className="ml-1">rm_stock_movements</code>.
            </DialogDescription>
          </DialogHeader>
          <Label className="text-xs uppercase">Reason (optional)</Label>
          <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="noinv-cancel-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionTarget(null); setCancelReason(""); }}>Back</Button>
            <Button onClick={doCancel} className="bg-rose-600 hover:bg-rose-700" data-testid="noinv-cancel-confirm">
              Confirm Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={actionTarget?.action === "delete"} onOpenChange={(o) => !o && setActionTarget(null)}>
        <DialogContent data-testid="noinv-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete draft entry {actionTarget?.entry?.entry_id}?</DialogTitle>
            <DialogDescription>
              Drafts have no stock impact. This permanently removes the row.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionTarget(null)}>Back</Button>
            <Button onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="noinv-delete-confirm">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ViewEntryDialog entry={viewEntry} onClose={() => setViewEntry(null)} />

      <BulkInvoiceCreateFromFileDialog
        open={showBulkFileDialog}
        onClose={() => setShowBulkFileDialog(false)}
        onStartCreateInvoice={onStartCreateInvoice}
      />
    </div>
  );
}

// ─── Read-only View dialog (Feb 2026) ────────────────────────────────────────
// Surfaces header + line items for any entry (DRAFT / SUBMITTED / CANCELLED /
// INVOICED) so stores teams can audit a row without entering edit mode.
function ViewEntryDialog({ entry, onClose }) {
  const open = !!entry;
  if (!entry) return null;
  const lines = entry.line_items || entry.items || [];
  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(); } catch { return String(s); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="noinv-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Entry {entry.entry_id}
            <Badge variant="outline" className={STATUS_STYLES[entry.status] || ""}>{entry.status}</Badge>
          </DialogTitle>
          <DialogDescription>
            Read-only view. Bill # <span className="font-mono">{entry.bill_number || "—"}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Vendor</Label>
            <div data-testid="noinv-view-vendor">{entry.vendor_name || entry.vendor_id || "—"}</div>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Branch</Label>
            <div data-testid="noinv-view-branch">{entry.branch || "—"}</div>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Entry Date</Label>
            <div>{entry.entry_date || entry.inward_date || "—"}</div>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Created</Label>
            <div>{fmtDate(entry.created_at)}</div>
          </div>
          {entry.submitted_at && (
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Submitted</Label>
              <div>{fmtDate(entry.submitted_at)}</div>
            </div>
          )}
          {entry.cancelled_at && (
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Cancelled</Label>
              <div>{fmtDate(entry.cancelled_at)}</div>
            </div>
          )}
          {entry.invoice_bill_id && (
            <div className="col-span-2">
              <Label className="text-xs uppercase text-muted-foreground">Linked Invoice</Label>
              <div className="font-mono text-xs">{entry.invoice_bill_id}</div>
            </div>
          )}
          {entry.cancel_reason && (
            <div className="col-span-2">
              <Label className="text-xs uppercase text-muted-foreground">Cancel Reason</Label>
              <div className="text-rose-700">{entry.cancel_reason}</div>
            </div>
          )}
          {entry.notes && (
            <div className="col-span-2">
              <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
              <div>{entry.notes}</div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <Label className="text-sm font-semibold mb-2 block">Line items</Label>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-44">RM ID</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-28 text-right">Quantity</TableHead>
                    <TableHead className="w-16">UOM</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-stone-500 py-6">
                        No line items recorded.
                      </TableCell>
                    </TableRow>
                  )}
                  {lines.map((li, idx) => {
                    const isFreight = (li.rm_id || "").toUpperCase() === "FREIGHT";
                    return (
                      <TableRow key={idx} data-testid={`noinv-view-line-${idx}`} className={isFreight ? "bg-amber-50/40" : ""}>
                        <TableCell className="font-mono text-xs">
                          {isFreight ? (
                            <Badge className="bg-amber-100 text-amber-900 border-amber-300 font-mono text-[10px]">FREIGHT</Badge>
                          ) : (li.rm_id || "—")}
                        </TableCell>
                        <TableCell className="text-xs">{li.description || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{li.quantity ?? "—"}</TableCell>
                        <TableCell className="text-xs">{li.unit || li.uom || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="noinv-view-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────
function buildEmptyForm() {
  return {
    vendor_id: "", vendor_name: "",
    branch: "", branch_id: "",
    bill_number: "",
    entry_date: new Date().toISOString().slice(0, 10),
    notes: "",
    line_items: [{ ...EMPTY_LINE }],
  };
}
function validateForm(form) {
  const errors = [];
  if (!form.vendor_id) errors.push("Vendor is required.");
  if (!form.branch) errors.push("Branch is required.");
  if (!form.bill_number.trim()) errors.push("Bill # is required (stores reference).");
  const lines = form.line_items.filter((li) => li.rm_id);
  if (lines.length === 0) errors.push("Add at least one RM line.");
  for (const li of lines) {
    if (!li.quantity || Number(li.quantity) <= 0) {
      errors.push(`Quantity > 0 required for ${li.rm_id}`);
      break;
    }
  }
  return errors;
}

function RowActions({ entry, onView, onEdit, onDeleteAsk, onSubmit, onCancelAsk, onCreateInvoice }) {
  const viewBtn = (
    <Button size="sm" variant="ghost" onClick={onView} data-testid={`noinv-view-${entry.entry_id}`}>
      <Eye className="w-3 h-3 mr-1" /> View
    </Button>
  );
  if (entry.status === "DRAFT") {
    return (
      <div className="flex justify-end gap-1">
        {viewBtn}
        <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`noinv-edit-${entry.entry_id}`}>
          <Edit2 className="w-3 h-3 mr-1" /> Edit
        </Button>
        <PermButton entity="RMInwardBill" action="CREATE" size="sm" onClick={onSubmit} data-testid={`noinv-submit-${entry.entry_id}`}>
          <Send className="w-3 h-3 mr-1" /> Submit
        </PermButton>
        <Button size="sm" variant="ghost" onClick={onDeleteAsk} data-testid={`noinv-del-${entry.entry_id}`}>
          <Trash2 className="w-3 h-3 text-rose-600" />
        </Button>
      </div>
    );
  }
  if (entry.status === "SUBMITTED") {
    return (
      <div className="flex justify-end gap-1">
        {viewBtn}
        <PermButton entity="RMInwardBill" action="CREATE" size="sm" variant="outline" onClick={onCreateInvoice} data-testid={`noinv-inv-${entry.entry_id}`}>
          <FileText className="w-3 h-3 mr-1" /> Create Invoice
        </PermButton>
        <Button size="sm" variant="ghost" onClick={onCancelAsk} data-testid={`noinv-cancel-${entry.entry_id}`}>
          <X className="w-3 h-3 text-rose-600 mr-1" /> Cancel
        </Button>
      </div>
    );
  }
  // Cancelled / Linked / other — still allow View
  return (
    <div className="flex justify-end">
      {viewBtn}
    </div>
  );
}

function EntryDialog({
  open, editingId, form, setForm, vendors, branches,
  onVendorChange, onBranchChange,
  setLine, addLine, addFreightLine, removeLine,
  rmSearch, setRmSearch, filteredRMs, pickRMForLine,
  onClose, onSaveDraft, onSaveAndSubmit, submitting,
}) {
  const [linePickerIdx, setLinePickerIdx] = useState(null);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="noinv-entry-dialog">
        <DialogHeader>
          <DialogTitle>{editingId ? `Edit Draft — ${editingId}` : "New No-Invoice Entry"}</DialogTitle>
          <DialogDescription>
            Receive RM stock without an invoice. Submitting credits inventory to the branch.
            A Zoho invoice can be drafted later via the "Create Invoice" action.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase">Vendor *</Label>
            <Select value={form.vendor_id} onValueChange={onVendorChange}>
              <SelectTrigger data-testid="noinv-form-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {vendors.map((v) => (
                  <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name || v.vendor_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">Branch *</Label>
            <Select value={form.branch} onValueChange={onBranchChange}>
              <SelectTrigger data-testid="noinv-form-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>
                {branches.map((b) => <SelectItem key={b.branch_id || b.name} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">Bill # (stores reference) *</Label>
            <Input
              value={form.bill_number}
              onChange={(e) => setForm({ ...form, bill_number: e.target.value })}
              placeholder="GRN-2026-001"
              data-testid="noinv-form-billno"
            />
          </div>
          <div>
            <Label className="text-xs uppercase">Date</Label>
            <Input
              type="date"
              value={form.entry_date}
              onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
              data-testid="noinv-form-date"
            />
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-sm font-semibold mb-2 block">Line items *</Label>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-44">RM ID</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-28 text-right">Quantity</TableHead>
                    <TableHead className="w-16">UOM</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.line_items.map((li, idx) => {
                    const isFreight = (li.rm_id || "").toUpperCase() === "FREIGHT";
                    return (
                      <TableRow key={idx} className={isFreight ? "bg-amber-50/40" : ""}>
                        <TableCell>
                          {isFreight ? (
                            <Badge className="bg-amber-100 text-amber-900 border-amber-300 font-mono text-[10px]">FREIGHT</Badge>
                          ) : (
                            <Button
                              size="sm" variant="outline"
                              className="font-mono text-xs h-8 w-full justify-start"
                              onClick={() => { setLinePickerIdx(idx); setRmSearch(""); }}
                              data-testid={`noinv-form-pickrm-${idx}`}
                            >
                              {li.rm_id || "Pick RM…"}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-stone-600">{li.description || "—"}</TableCell>
                        <TableCell>
                          <Input
                            type="number" min="0" step="0.001" value={li.quantity}
                            onChange={(e) => setLine(idx, { quantity: e.target.value })}
                            className="h-8 text-right"
                            data-testid={`noinv-form-qty-${idx}`}
                          />
                        </TableCell>
                        <TableCell className="text-xs">{li.unit || "—"}</TableCell>
                        <TableCell>
                          {form.line_items.length > 1 && (
                            <Button size="sm" variant="ghost" onClick={() => removeLine(idx)} data-testid={`noinv-form-removeline-${idx}`}>
                              <X className="w-3 h-3 text-rose-600" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {/* Add-line and Add-freight controls live BELOW the table to mirror
              the existing RM Inward (with-invoice) dialog UX. */}
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={addLine} data-testid="noinv-form-addline">
              <Plus className="w-3 h-3 mr-1" /> Add line
            </Button>
            <Button size="sm" variant="outline" onClick={addFreightLine} data-testid="noinv-form-addfreight" className="border-amber-300 text-amber-800 hover:bg-amber-50">
              <Plus className="w-3 h-3 mr-1" /> Add freight
            </Button>
          </div>
        </div>

        <Dialog open={linePickerIdx !== null} onOpenChange={(o) => !o && setLinePickerIdx(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Pick RM</DialogTitle>
            </DialogHeader>
            <Input
              placeholder="Search RM ID or description…" value={rmSearch}
              onChange={(e) => setRmSearch(e.target.value)} autoFocus
              data-testid="noinv-form-rmsearch"
            />
            <div className="max-h-96 overflow-y-auto border rounded mt-2">
              <Table>
                <TableBody>
                  {filteredRMs.map((rm) => (
                    <TableRow key={rm.rm_id} className="cursor-pointer hover:bg-stone-50"
                      onClick={() => { pickRMForLine(linePickerIdx, rm); setLinePickerIdx(null); }}
                      data-testid={`noinv-form-rmopt-${rm.rm_id}`}
                    >
                      <TableCell className="font-mono text-xs w-32">{rm.rm_id}</TableCell>
                      <TableCell className="text-xs">{rm.description || rm.name || ""}</TableCell>
                      <TableCell className="text-xs text-stone-500 w-16">{rm.uom || rm.procurement_uom || ""}</TableCell>
                    </TableRow>
                  ))}
                  {filteredRMs.length === 0 && (
                    <TableRow><TableCell colSpan={3} className="text-center text-stone-500 py-8">No matches.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </DialogContent>
        </Dialog>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onSaveDraft} disabled={submitting} data-testid="noinv-form-savedraft">
            {submitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            {editingId ? "Save draft" : "Save as Draft"}
          </Button>
          <Button onClick={onSaveAndSubmit} disabled={submitting} data-testid="noinv-form-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Send className="w-3 h-3 mr-1" /> Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
